// 7 genuinely distinct storefront design systems (from design_guidelines.json blueprint).
// Each template = full token set + structural variants. Agent overrides merge on top.

export const TEMPLATES = {
  modern_luxury: {
    id: "modern_luxury", num: "01", name: "Modern Luxury",
    tagline: "Editorial minimalism, serif typography, high-fashion whitespace",
    dark: false,
    tokens: {
      bg: "#FAF9F6", surface: "#FFFFFF", border: "rgba(26,26,26,0.12)",
      text: "#1A1A1A", textSecondary: "#666666", muted: "#8C8C8C",
      primary: "#1A1A1A", secondary: "#D4AF37", accent: "#C5A059",
      buttonBg: "#1A1A1A", buttonText: "#FAF9F6", buttonHover: "#C5A059",
      badgeBg: "#F4F1EA", badgeText: "#1A1A1A",
      announceBg: "#1A1A1A", announceText: "#FAF9F6",
      footerBg: "#1A1A1A", footerText: "#FAF9F6", footerAccent: "#C5A059",
    },
    fonts: {
      heading: "'Cormorant Garamond', serif", body: "'Montserrat', sans-serif", button: "'Montserrat', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600&display=swap",
    },
    radius: { card: "0px", button: "0px", input: "0px" },
    heroLayout: "split-editorial", cardVariant: "luxury", categoryVariant: "strips",
    bannerVariant: "mono-gold", headerVariant: "centered", footerVariant: "watermark",
    sectionOrder: ["hero", "categories", "featured_collection", "new_arrivals", "promo_banner", "best_sellers", "lifestyle", "reviews", "newsletter"],
  },
  color_pop: {
    id: "color_pop", num: "02", name: "Color Pop",
    tagline: "Neo-brutalist energy, bold strokes, vibrant gradients",
    dark: false,
    tokens: {
      bg: "#FFF9E6", surface: "#FFFFFF", border: "#000000",
      text: "#0F172A", textSecondary: "#334155", muted: "#64748B",
      primary: "#FF3366", secondary: "#FFD000", accent: "#00E5FF",
      buttonBg: "#FF3366", buttonText: "#FFFFFF", buttonHover: "#00E5FF",
      badgeBg: "#FFD000", badgeText: "#0F172A",
      announceBg: "#00E5FF", announceText: "#0F172A",
      footerBg: "#FF3366", footerText: "#FFFFFF", footerAccent: "#FFD000",
    },
    fonts: {
      heading: "'Space Grotesk', sans-serif", body: "'Plus Jakarta Sans', sans-serif", button: "'Space Grotesk', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;700&display=swap",
    },
    radius: { card: "20px", button: "999px", input: "16px" },
    heroLayout: "bento", cardVariant: "pop", categoryVariant: "bubbles",
    bannerVariant: "sticker", headerVariant: "pill", footerVariant: "wave",
    sectionOrder: ["hero", "categories", "flash_deals", "featured_products", "promo_banner", "best_sellers", "reviews", "newsletter"],
  },
  editorial_lifestyle: {
    id: "editorial_lifestyle", num: "03", name: "Editorial Lifestyle",
    tagline: "Magazine aesthetics, warm linen neutrals, story-driven layout",
    dark: false,
    tokens: {
      bg: "#F7F4EF", surface: "#EFECE6", border: "#D8D3C8",
      text: "#2C2A29", textSecondary: "#595552", muted: "#88827D",
      primary: "#4A3B32", secondary: "#8C6D58", accent: "#BFA38E",
      buttonBg: "#2C2A29", buttonText: "#F7F4EF", buttonHover: "#8C6D58",
      badgeBg: "#E5DEC9", badgeText: "#2C2A29",
      announceBg: "#4A3B32", announceText: "#F7F4EF",
      footerBg: "#EFECE6", footerText: "#2C2A29", footerAccent: "#8C6D58",
    },
    fonts: {
      heading: "'Playfair Display', serif", body: "'Lato', sans-serif", button: "'Lato', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;1,400&family=Playfair+Display:ital,wght@0,500;0,700;1,400&display=swap",
    },
    radius: { card: "4px", button: "4px", input: "4px" },
    heroLayout: "magazine", cardVariant: "editorial", categoryVariant: "articles",
    bannerVariant: "linen", headerVariant: "twotier", footerVariant: "colophon",
    sectionOrder: ["hero", "lifestyle", "featured_collection", "categories", "featured_products", "promo_banner", "best_sellers", "reviews", "newsletter"],
  },
  trendy_fashion: {
    id: "trendy_fashion", num: "04", name: "Trendy Fashion",
    tagline: "Streetwear lookbook, high contrast, kinetic energy",
    dark: true,
    tokens: {
      bg: "#0D0D0D", surface: "#1A1A1A", border: "#333333",
      text: "#FFFFFF", textSecondary: "#A3A3A3", muted: "#737373",
      primary: "#E11D48", secondary: "#FFFFFF", accent: "#F43F5E",
      buttonBg: "#FFFFFF", buttonText: "#0D0D0D", buttonHover: "#E11D48",
      badgeBg: "#E11D48", badgeText: "#FFFFFF",
      announceBg: "#E11D48", announceText: "#FFFFFF",
      footerBg: "#000000", footerText: "#FFFFFF", footerAccent: "#E11D48",
    },
    fonts: {
      heading: "'Bebas Neue', sans-serif", body: "'Inter', sans-serif", button: "'Bebas Neue', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;600;800&display=swap",
    },
    radius: { card: "0px", button: "0px", input: "0px" },
    heroLayout: "fullbleed", cardVariant: "fashion", categoryVariant: "lookbook",
    bannerVariant: "drop", headerVariant: "stark", footerVariant: "ticker",
    sectionOrder: ["hero", "flash_deals", "featured_collection", "categories", "new_arrivals", "promo_banner", "best_sellers", "reviews", "newsletter"],
  },
  tech_futuristic: {
    id: "tech_futuristic", num: "05", name: "Tech Futuristic",
    tagline: "Cyberpunk command center, glassmorphism, neon glow",
    dark: true,
    tokens: {
      bg: "#0A0D14", surface: "rgba(16,22,34,0.75)", border: "rgba(0,240,255,0.2)",
      text: "#F0F6FC", textSecondary: "#8B949E", muted: "#484F58",
      primary: "#00F0FF", secondary: "#7000FF", accent: "#00FF66",
      buttonBg: "#00F0FF", buttonText: "#0A0D14", buttonHover: "#00FF66",
      badgeBg: "rgba(0,240,255,0.15)", badgeText: "#00F0FF",
      announceBg: "#0A0D14", announceText: "#00FF66",
      footerBg: "#05070A", footerText: "#F0F6FC", footerAccent: "#00F0FF",
    },
    fonts: {
      heading: "'Orbitron', sans-serif", body: "'JetBrains Mono', monospace", button: "'Orbitron', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Orbitron:wght@600;800;900&display=swap",
    },
    radius: { card: "8px", button: "6px", input: "6px" },
    heroLayout: "hud", cardVariant: "tech", categoryVariant: "matrix",
    bannerVariant: "cyber", headerVariant: "glass", footerVariant: "terminal",
    sectionOrder: ["hero", "categories", "featured_products", "comparison", "flash_deals", "promo_banner", "best_sellers", "reviews", "newsletter"],
  },
  natural_organic: {
    id: "natural_organic", num: "06", name: "Natural Organic",
    tagline: "Botanical wellness, sage & terracotta, soft organic curves",
    dark: false,
    tokens: {
      bg: "#F4F7F2", surface: "#FFFFFF", border: "#D2DDD0",
      text: "#1C3322", textSecondary: "#3E5745", muted: "#758C7C",
      primary: "#2D5A27", secondary: "#C86D51", accent: "#8FB339",
      buttonBg: "#2D5A27", buttonText: "#FFFFFF", buttonHover: "#C86D51",
      badgeBg: "#E2EBDD", badgeText: "#1C3322",
      announceBg: "#2D5A27", announceText: "#FFFFFF",
      footerBg: "#1C3322", footerText: "#F4F7F2", footerAccent: "#8FB339",
    },
    fonts: {
      heading: "'Lora', serif", body: "'DM Sans', sans-serif", button: "'DM Sans', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Lora:ital,wght@0,500;0,600;1,400&display=swap",
    },
    radius: { card: "16px", button: "999px", input: "12px" },
    heroLayout: "arch", cardVariant: "organic", categoryVariant: "pebbles",
    bannerVariant: "earth", headerVariant: "sage", footerVariant: "botanical",
    sectionOrder: ["hero", "why_us", "featured_collection", "categories", "featured_products", "lifestyle", "best_sellers", "promo_banner", "reviews", "newsletter"],
  },
  fun_playful: {
    id: "fun_playful", num: "07", name: "Fun & Playful",
    tagline: "Joyful family universe, bubbly shapes, sunny palette",
    dark: false,
    tokens: {
      bg: "#FFFDF5", surface: "#FFFFFF", border: "#FFE5A3",
      text: "#2D2342", textSecondary: "#5C4D78", muted: "#8E7DAA",
      primary: "#FF6B6B", secondary: "#4ECDC4", accent: "#FFE66D",
      buttonBg: "#FF6B6B", buttonText: "#FFFFFF", buttonHover: "#4ECDC4",
      badgeBg: "#FFE66D", badgeText: "#2D2342",
      announceBg: "#4ECDC4", announceText: "#FFFFFF",
      footerBg: "#2D2342", footerText: "#FFFDF5", footerAccent: "#FFE66D",
    },
    fonts: {
      heading: "'Fredoka', sans-serif", body: "'Nunito', sans-serif", button: "'Fredoka', sans-serif",
      importUrl: "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap",
    },
    radius: { card: "24px", button: "999px", input: "16px" },
    heroLayout: "storybook", cardVariant: "playful", categoryVariant: "cubbies",
    bannerVariant: "club", headerVariant: "sunny", footerVariant: "playground",
    sectionOrder: ["hero", "categories", "featured_products", "flash_deals", "best_sellers", "promo_banner", "reviews", "lifestyle", "newsletter"],
  },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);

export const SECTION_DEFS = [
  { type: "announcement", label: "Announcement Bar", fields: ["text"] },
  { type: "hero", label: "Hero", fields: ["badge", "heading", "subheading", "cta_text", "cta_url", "image"] },
  { type: "categories", label: "Categories", fields: ["heading"] },
  { type: "featured_products", label: "Featured Products", fields: ["heading"] },
  { type: "new_arrivals", label: "New Arrivals", fields: ["heading"] },
  { type: "flash_deals", label: "Flash Deals", fields: ["heading", "ends_text"] },
  { type: "promo_banner", label: "Promotional Banner", fields: ["heading", "subheading", "cta_text", "cta_url", "image"] },
  { type: "best_sellers", label: "Best Sellers", fields: ["heading"] },
  { type: "lifestyle", label: "Lifestyle / Story", fields: ["heading", "text", "image"] },
  { type: "featured_collection", label: "Featured Collection", fields: ["heading", "subheading"] },
  { type: "reviews", label: "Customer Reviews", fields: ["heading"] },
  { type: "instagram_gallery", label: "Gallery", fields: ["heading"] },
  { type: "why_us", label: "Why Choose Us", fields: ["heading"] },
  { type: "comparison", label: "Comparison", fields: ["heading"] },
  { type: "newsletter", label: "Newsletter", fields: ["heading", "subheading"] },
];

export const AI_PROMPT_SUGGESTIONS = [
  "Premium fashion lifestyle photography",
  "Modern luxury home interior",
  "Colorful electronics promotional banner",
  "Minimal product advertisement",
  "Festive shopping sale banner",
  "Summer fashion collection",
  "Premium beauty campaign",
  "Modern kitchen lifestyle",
];

export const FONT_OPTIONS = [
  "Cormorant Garamond", "Playfair Display", "Bebas Neue", "Orbitron", "Lora", "Fredoka",
  "Montserrat", "Plus Jakarta Sans", "Lato", "Inter", "JetBrains Mono", "DM Sans", "Nunito", "Space Grotesk",
];
